import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { AnchorIcon } from "@/components/icons/AnchorIcon";
import analytics from "@/lib/segment";

type PlanTier = "free" | "pro" | "scale" | "fleet";
type BillingInterval = "monthly" | "annual";

/**
 * Lightweight full-screen page shown between signup and Stripe Checkout.
 * Lives outside /app/* so no AppLayout, sidebar, or onboarding overlay renders.
 */
export default function CheckoutRedirect() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { isLoading: authLoading, isAuthenticated } = useAuth({ requireAuth: true, redirectTo: "/auth" });
  const hasTriggered = useRef(false);

  const plan = searchParams.get("plan") as PlanTier | null;
  const billing = (searchParams.get("billing") as BillingInterval) || "monthly";
  const sourceDiscount = searchParams.get("source") || undefined;

  useEffect(() => {
    if (authLoading || !isAuthenticated || hasTriggered.current) return;
    if (!plan || plan === "free") {
      navigate("/app/dashboard", { replace: true });
      return;
    }

    hasTriggered.current = true;

    const redirect = async () => {
      try {
        // Clear the intended tier so the user won't be redirected again on next login
        try {
          const { data: membership } = await supabase
            .from("company_members")
            .select("company_id")
            .eq("user_id", (await supabase.auth.getUser()).data.user?.id || "")
            .maybeSingle();

          if (membership?.company_id) {
            await supabase
              .from("companies")
              .update({ intended_subscription_tier: null, intended_source_discount: null })
              .eq("id", membership.company_id);
          }
        } catch (err) {
          console.error("Failed to clear intended tier:", err);
        }

        analytics.track("Checkout Started", {
          plan_tier: plan,
          billing_interval: billing,
          source: "checkout_redirect",
          ...(sourceDiscount ? { source_discount: sourceDiscount } : {}),
        });

        const response = await supabase.functions.invoke("create-checkout", {
          body: {
            tier: plan,
            billingInterval: billing,
            returnUrl: window.location.origin,
            ...(sourceDiscount ? { sourceDiscount } : {}),
          },
        });

        if (response.error) {
          throw new Error(response.error.message || "Failed to create checkout session");
        }
        if (response.data?.error) {
          throw new Error(response.data.error);
        }
        if (response.data?.url) {
          window.location.href = response.data.url;
        } else {
          throw new Error("No checkout URL received");
        }
      } catch (err: any) {
        analytics.track("Error Occurred", {
          error_type: "checkout",
          error_message: err.message,
          plan_tier: plan,
        });
        toast({
          title: "Checkout error",
          description: err.message || "Something went wrong. Please try again.",
          variant: "destructive",
        });
        navigate("/pricing", { replace: true });
      }
    };

    redirect();
  }, [authLoading, isAuthenticated, plan, billing, sourceDiscount, navigate, toast]);

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
      <AnchorIcon variant="blue" className="h-10 w-10" />
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <p className="text-muted-foreground text-sm">
        Setting up your {plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : ''} plan…
      </p>
    </div>
  );
}
